// Settle audio engine.
//
// SFX: synthesized via the existing Web Audio patches.
// MUSIC: Howler-loaded files driven by a simple on/off Radio that
// shuffles through every entry in PLAYLIST.

import { Howl, Howler } from 'howler';
import { AudioEngine as SynthEngine } from '../audio/audio';
import type { AudioSettings, MusicVariant, SfxName } from '../audio/audio';
import { PLAYLIST } from './playlist';
import type { PlaylistTrack } from './playlist';

export type { AudioSettings, SfxName };

export interface RadioState {
  enabled: boolean;
}

export const DEFAULT_RADIO_STATE: RadioState = { enabled: true };

export class HowlerAudioEngine {
  private synth = new SynthEngine();
  private currentHowl: Howl | null = null;
  private currentTrackId: string | null = null;
  /** Fires whenever a track begins playing (after the initial fade-in
   *  starts). Used by the now-playing toast. */
  onTrackStart: ((track: PlaylistTrack) => void) | null = null;
  private queue: string[] = [];
  private recentHistory: string[] = [];
  private retiring: Set<Howl> = new Set();
  radio: RadioState = { ...DEFAULT_RADIO_STATE };
  settings: AudioSettings;

  /** Low-pass filter inserted into Howler's master output. When the
   *  in-game pause menu opens we ramp the cutoff down to dull the music. */
  private musicFilter: BiquadFilterNode | null = null;
  /** Identity reference to the Howler masterGain node we wired our filter
   *  onto. If Howler ever recreates its masterGain (some Howl lifecycle
   *  paths trigger this), we'll detect the change and re-route. */
  private hookedMasterGain: GainNode | null = null;
  private musicDuckGain: GainNode | null = null;
  private duckLevel = 0;
  /** Analyser tapped into the music chain (Howler carries only the
   *  radio — SFX live on the synth's own context), feeding the
   *  miniplayer's visualizer bars. Recreated alongside the duck chain
   *  whenever Howler rebuilds its master node. */
  private analyser: AnalyserNode | null = null;

  constructor() {
    this.settings = this.synth.settings;
    Howler.autoUnlock = true;
    Howler.html5PoolSize = 4;
  }

  init(): void {
    this.synth.init();
    Howler.volume(this.settings.muted ? 0 : this.settings.master);
    this.ensureMusicFilter();
  }

  /** Insert (or re-insert) the duck low-pass between Howler.masterGain and
   *  the destination. Called every time setMusicDuck fires so Howler-driven
   *  graph teardowns (Howl unload, track switch, context resume) can't
   *  leave us with a stale or detached filter chain. */
  private ensureMusicFilter(): void {
    const hh = Howler as unknown as { ctx?: AudioContext; masterGain?: GainNode };
    const ctx = hh.ctx;
    const masterGain = hh.masterGain;
    if (!ctx || !masterGain) return;
    // Already wired onto this exact masterGain — nothing to do.
    if (this.hookedMasterGain === masterGain && this.musicFilter && this.musicDuckGain) return;
    // Tear down any prior filter so we don't pile up nodes.
    if (this.musicFilter) try { this.musicFilter.disconnect(); } catch { /* */ }
    if (this.musicDuckGain) try { this.musicDuckGain.disconnect(); } catch { /* */ }
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    // If we were already ducked, preserve the cutoff so the audible state
    // doesn't jump when we re-insert mid-pause.
    const lv = this.duckLevel;
    filter.frequency.value = 650 + (22000 - 650) * Math.pow(1 - lv, 2.2);
    filter.Q.value = 0.7;
    const duck = ctx.createGain();
    duck.gain.value = 1 - 0.36 * lv;
    // Analyser is a passthrough at the end of the chain — the
    // miniplayer's visualizer samples it.
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.72;
    try { masterGain.disconnect(); } catch { /* */ }
    masterGain.connect(filter);
    filter.connect(duck);
    duck.connect(analyser);
    analyser.connect(ctx.destination);
    this.musicFilter = filter;
    this.musicDuckGain = duck;
    this.analyser = analyser;
    this.hookedMasterGain = masterGain;
  }

  /** Live analyser over the music chain, or null before audio init.
   *  Consumers should re-call rather than cache — the node is rebuilt
   *  if Howler recreates its master gain. */
  musicAnalyser(): AnalyserNode | null {
    this.ensureMusicFilter();
    return this.analyser;
  }

  /** Set music duck level. Optional rampSeconds controls the transition
   *  length — callers default to a snappy 180ms when ducking down (entering
   *  pause), but pass a longer value (e.g. 1.4s) when unducking back to 0
   *  so the filter sweeps gently rather than snapping the high end back. */
  setMusicDuck(level: number, rampSeconds = 0.18): void {
    this.duckLevel = Math.max(0, Math.min(1, level));
    // Always re-ensure — if Howler rebuilt masterGain since we last hooked,
    // this will detect the new node and re-route. Without this, the filter
    // can silently stop affecting music after a Howl unload.
    this.ensureMusicFilter();
    if (!this.musicFilter || !this.musicDuckGain) return;
    const ctx = (Howler as unknown as { ctx: AudioContext }).ctx;
    const now = ctx.currentTime;
    const cutoff = 650 + (22000 - 650) * Math.pow(1 - this.duckLevel, 2.2);
    const gain = 1 - 0.36 * this.duckLevel;
    const ramp = Math.max(0.02, rampSeconds);
    this.musicFilter.frequency.cancelScheduledValues(now);
    this.musicFilter.frequency.setValueAtTime(this.musicFilter.frequency.value, now);
    this.musicFilter.frequency.linearRampToValueAtTime(cutoff, now + ramp);
    this.musicDuckGain.gain.cancelScheduledValues(now);
    this.musicDuckGain.gain.setValueAtTime(this.musicDuckGain.gain.value, now);
    this.musicDuckGain.gain.linearRampToValueAtTime(gain, now + ramp);
  }

  setSettings(s: Partial<AudioSettings>): void {
    this.synth.setSettings(s);
    this.settings = this.synth.settings;
    Howler.volume(this.settings.muted ? 0 : this.settings.master);
    if (this.currentHowl) this.currentHowl.volume(this.settings.music);
  }

  play(name: SfxName, intensity = 1): void {
    this.synth.play(name, intensity);
  }

  /** Continuous purr voice (cat companion hover). */
  startPurr(): void { this.synth.startPurr(); }
  stopPurr(): void { this.synth.stopPurr(); }

  // ─── Radio control ────────────────────────────────────────────

  setRadioState(s: RadioState): void {
    const wasEnabled = this.radio.enabled;
    this.radio = { enabled: s.enabled };
    if (!s.enabled) { this.stopRadio(); return; }
    if (!wasEnabled) this.playNext();
  }

  startRadio(): void {
    if (!this.radio.enabled) return;
    if (this.currentHowl && this.currentHowl.playing()) return;
    this.playNext();
  }

  stopRadio(): void {
    if (this.currentHowl) {
      const h = this.currentHowl;
      h.fade(this.settings.music, 0, 220);
      this.retiring.add(h);
      setTimeout(() => { try { h.unload(); } catch { /* */ } this.retiring.delete(h); }, 300);
      this.currentHowl = null;
      this.currentTrackId = null;
    }
    this.queue.length = 0;
  }

  // ─── Transport (miniplayer) ───────────────────────────────────

  /** Skip to the next shuffled track. */
  skipNext(): void {
    if (!this.radio.enabled) return;
    this.playNext();
  }

  /** Replay the previous track from history (falls back to a skip
   *  when there's no history yet). */
  skipPrev(): void {
    if (!this.radio.enabled) return;
    // recentHistory's last entry is the CURRENT track; the one before
    // it is the previous. Drop the current entry so prev-prev keeps
    // walking backwards on repeated presses.
    if (this.recentHistory.length >= 2) {
      this.recentHistory.pop();
      const prevId = this.recentHistory.pop();
      const track = PLAYLIST.find((t) => t.id === prevId);
      if (track) { this.loadAndPlay(track); return; }
    }
    this.playNext();
  }

  /** Pause/resume the current track in place. Returns the new
   *  playing state. Starts the radio if nothing is loaded yet. */
  toggleRadioPause(): boolean {
    if (!this.currentHowl) {
      this.startRadio();
      return true;
    }
    if (this.currentHowl.playing()) {
      this.currentHowl.pause();
      return false;
    }
    this.currentHowl.play();
    return true;
  }

  radioPlaying(): boolean {
    return this.currentHowl?.playing() ?? false;
  }

  /** Title of the track currently loaded (paused or playing). */
  currentTrackTitle(): string | null {
    const t = PLAYLIST.find((x) => x.id === this.currentTrackId);
    return t?.title ?? null;
  }

  // ─── Queue + playback ─────────────────────────────────────────

  private playNext(): void {
    if (PLAYLIST.length === 0) { this.stopRadio(); return; }
    if (this.queue.length === 0) {
      // Fisher-Yates shuffle for a fresh deck.
      const ids = PLAYLIST.map((t) => t.id);
      for (let i = ids.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const t = ids[i]!; ids[i] = ids[j]!; ids[j] = t;
      }
      // Push every recently-played track toward the BACK of the deck
      // so the front is biased toward fresher selections. recentHistory
      // holds the last ~10 plays — partitioning by it means a session
      // restart can't immediately replay the last few tracks, and
      // the deck-shuffle boundary doesn't put a just-played track at
      // the head of the next round. The currently-playing id is also
      // forced into the recent bucket so it can never sit at the head
      // of the refreshed queue (was a real bug — when the current track
      // ended just before recentHistory had absorbed it, the partition
      // saw it as 'fresh' and replayed it immediately).
      if (ids.length > 4 && (this.recentHistory.length > 0 || this.currentTrackId)) {
        const recentSet = new Set(this.recentHistory);
        if (this.currentTrackId) recentSet.add(this.currentTrackId);
        const fresh: string[] = [];
        const recent: string[] = [];
        for (const id of ids) {
          if (recentSet.has(id)) recent.push(id);
          else fresh.push(id);
        }
        // Re-shuffle the recent group so the back ordering varies too.
        for (let i = recent.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          const t = recent[i]!; recent[i] = recent[j]!; recent[j] = t;
        }
        this.queue = [...fresh, ...recent];
      } else {
        this.queue = ids;
      }
    }
    let id = this.queue.shift();
    if (!id) return;
    // Belt-and-suspenders: if the dequeued track is the one we're already
    // playing (can happen if the deck just refilled while it was still
    // loading), skip it and pull the next entry instead. Without this
    // guard a single track could pin the radio to itself on loop.
    if (id === this.currentTrackId && this.queue.length > 0) {
      this.queue.push(id);
      id = this.queue.shift()!;
    }
    const track = PLAYLIST.find((t) => t.id === id);
    if (!track) { this.playNext(); return; }
    // Mark as recently-played at dequeue time, not in onload. The previous
    // arrangement left a window where the next playNext could see this
    // track as fresh again because loading hadn't completed yet.
    this.recentHistory.push(track.id);
    if (this.recentHistory.length > 10) this.recentHistory.shift();
    this.loadAndPlay(track);
  }

  private loadAndPlay(track: PlaylistTrack): void {
    const h = new Howl({
      src: [track.url],
      html5: false,
      loop: false,
      volume: 0,
      onload: () => {
        // Re-check our filter wiring after every load — Howler can rebuild
        // its master node between Howls, which would orphan our duck chain.
        this.ensureMusicFilter();
        if (this.currentHowl && this.currentHowl !== h) {
          const old = this.currentHowl;
          old.fade(this.settings.music, 0, 500);
          this.retiring.add(old);
          setTimeout(() => { try { old.unload(); } catch { /* */ } this.retiring.delete(old); }, 600);
        }
        this.currentHowl = h;
        this.currentTrackId = track.id;
        h.play();
        h.fade(0, this.settings.music, 600);
        // Re-ensure once more after the play call — Howler creates the
        // playback chain at play() time, and on some browsers this is the
        // first moment masterGain truly exists.
        this.ensureMusicFilter();
        try { this.onTrackStart?.(track); } catch { /* observer */ }
      },
      onend: () => {
        if (this.currentHowl === h) this.playNext();
      },
      onloaderror: () => {
        // Missing file — skip to next.
        this.playNext();
      },
    });
  }

  // Backwards-compat shims (other code paths still call these).
  startMusic(_variant?: MusicVariant): void {
    if (this.radio.enabled) this.startRadio();
  }
  stopMusic(): void { this.stopRadio(); }
  setMusicLevel(level: number): void { this.synth.setMusicLevel(level); }
}
